import { EditableList, ReadingListDetails } from '®shared/reading-list/models';

import { Permissions, grantThisRolePermission } from './permissions';
import { RoleId } from './roles';
import { UserPermissions } from './user-permissions';

/**
 * A utility to determine whether a user has a permission against a list by ID, or globally.
 *
 * @param {string} permissionName - The permission to be tested
 * @param {string} listId - The list ID to check
 * @param {UserPermissions} permissions - The permissions object for the user
 * @returns {boolean} - True if the user has the supplied permission against the list or globally.
 */
export function hasPermissionForListId(
  permissionName: string,
  listId: string,
  permissions: UserPermissions,
): boolean {
  return permissions.hasPermission(permissionName, `lists:${listId}`);
}

/**
 * Does the user have any of the following permissions for a given list ID?
 *
 * @param  {string[]}        permissionNames A list of permission names
 * @param  {string}          listId          The list ID to check
 * @param  {UserPermissions} permissions     The permissions object for the user
 * @return {boolean}                         True if the user has at least one of the supplied permissions against the list, or globally.
 */
export function hasAnyPermissionForListId(
  permissionNames: string[],
  listId: string,
  permissions: UserPermissions,
): boolean {
  return permissionNames.some(permissionName =>
    hasPermissionForListId(permissionName, listId, permissions),
  );
}

/**
 * A utility to determine whether a user has permission to grant a role to other users
 * for the given list, the nodes it belongs to in the hierarchy, or globally.
 *
 * @param {RoleId} roleId - The roleId to be tested
 * @param {string} listId - The list ID to check
 * @param {UserPermissions} permissions - The permissions object for the user
 * @returns {boolean} - True if the user has permission to grant the given role for this list
 */
export function hasGrantRolePermissionForListId(
  roleId: RoleId,
  listId: string,
  permissions: UserPermissions,
): boolean {
  const hasGrantAnyRolePermissionForList = hasPermissionForListId(
    Permissions.GRANT_ANY_ROLE,
    listId,
    permissions,
  );

  const hasGrantThisRolePermissionForList = hasPermissionForListId(
    grantThisRolePermission(permissions.convertRoleIdToRoleCode(roleId)),
    listId,
    permissions,
  );
  return hasGrantAnyRolePermissionForList || hasGrantThisRolePermissionForList;
}

/**
 * Returns a map of properties controlling the appearance of the 'Edit' menu and its children.
 * @param {ReadingListDetails} list - The list for which the user may need to edit.
 * @param {EditableList} editableList - The editable list store
 * @param {UserPermissions} currentUserPermissions - Permissions object to be tested.
 * @returns {EditMenuVisibility} - An object enumerating edit menu tasks
 */
export function getEditMenuVisibility(
  list: ReadingListDetails,
  editableList: EditableList,
  currentUserPermissions: UserPermissions,
): EditMenuVisibility {
  return {
    canArchiveList:
      !list.is_archived &&
      (editableList.hasEverBeenPublished ? true : false) &&
      hasPermissionForListId(
        Permissions.ARCHIVE_LIST,
        editableList.id,
        currentUserPermissions,
      ),
    canAssignListOwners:
      !list.is_archived &&
      hasAnyPermissionForListId(
        [Permissions.PUBLISH_LIST, Permissions.EDIT_LIST],
        editableList.id,
        currentUserPermissions,
      ),
    canAttachListToHierarchy:
      !list.is_archived &&
      hasPermissionForListId(
        Permissions.ATTACH_LIST_TO_NODE,
        editableList.id,
        currentUserPermissions,
      ),
    canModifyHierarchyAndStudentNumbers:
      !list.is_archived &&
      hasAnyPermissionForListId(
        [Permissions.ATTACH_LIST_TO_NODE, Permissions.EDIT_LIST],
        editableList.id,
        currentUserPermissions,
      ),
    canCreateList: hasPermissionForListId(
      Permissions.CREATE_LIST,
      editableList.id,
      currentUserPermissions,
    ),
    canDeleteList: hasPermissionForListId(
      Permissions.DELETE_LIST,
      editableList.id,
      currentUserPermissions,
    ),
    canEditList:
      !list.is_archived &&
      hasPermissionForListId(
        Permissions.EDIT_LIST,
        editableList.id,
        currentUserPermissions,
      ),
    canInviteListPublishers:
      !list.is_archived &&
      hasGrantRolePermissionForListId(
        RoleId.LIST_PUBLISHER,
        editableList.id,
        currentUserPermissions,
      ),
    canReviewList:
      !list.is_archived &&
      hasPermissionForListId(
        Permissions.VIEW_ACQUISITIONS,
        editableList.id,
        currentUserPermissions,
      ),
    canRequestReview:
      !list.is_archived &&
      !editableList.activeReviewUrl &&
      hasPermissionForListId(
        Permissions.REQUEST_REVIEW,
        editableList.id,
        currentUserPermissions,
      ),
    hasSuperuserRole: currentUserPermissions.hasSuperuserRole(),
  };
}

/**
 * @param {ReadingListDetails} list - The list for which the user may need to edit.
 * @param {EditableList} editableList - The editable list store
 * @param {UserPermissions} currentUserPermissions - Permissions object to be tested.
 * @returns {boolean} - true if the user has permission to perform any list edit action.
 */
export function hasAnyListEditPermission(
  list: ReadingListDetails,
  editableList: EditableList,
  currentUserPermissions: UserPermissions,
): boolean {
  const menuMap = getEditMenuVisibility(
    list,
    editableList,
    currentUserPermissions,
  );
  return Object.keys(menuMap).some(key => menuMap[key]);
}

export type EditMenuVisibility = {
  canArchiveList: boolean;
  canAssignListOwners: boolean;
  canAttachListToHierarchy: boolean;
  canCreateList: boolean;
  canDeleteList: boolean;
  canEditList: boolean;
  canInviteListPublishers: boolean;
  canModifyHierarchyAndStudentNumbers: boolean;
  canReviewList: boolean;
  canRequestReview: boolean;
  hasSuperuserRole: boolean;
};
